import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

// Types
import { ReviewState } from 'Shared/types/Review.type';
import { FormTypeType } from 'Shared/types/ReviewFormTemplate.type';
import { ReviewTemplateTypeType } from 'Shared/types/ReviewTemplates.type';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Helpers
import * as ReviewHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';

// Utils
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';

// Selectors
import * as ReviewsSelectors from 'Shared/selectors/Reviews.selectors';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import CoachingPageHeader from '../_partials/CoachingPageHeader';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import ReviewsCardsContainer from 'Shared/components/ReviewsCardsContainer/ReviewsCardsContainer';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

// Styles
import './OpenReviews.scss';

const OpenReviews = () => {
  const history = useHistory();

  const { user: currentUser, isManager } = useCurrentUser();

  const openReviews = useSelector(state =>
    ReviewsSelectors.getReviewsByStatus(state, {
      status: [ReviewState.PEER_REVIEW, ReviewState.COMPILATION, ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED],
      reviewTemplateType: ReviewTemplateTypeType.review360,
    })
  );

  const openReviewsAsCompilerOrCreator = openReviews.filter(review => {
    return (
      ReviewsUtils.isCurrentUserCompiler({ review, currentUserSlug: currentUser.slug }) ||
      ReviewsUtils.isCurrentUserCreator({ review, currentUserSlug: currentUser.slug })
    );
  });

  const openReviewSlugs = openReviews
    ?.valueSeq()
    .map(review => ReviewHelpers.getSlug(review))
    ?.toJS();

  const selfReviewFormsForCurrentUser = useSelector(state =>
    ReviewsSelectors.getReviewFormsByUserSlugAndType(state, {
      formType: FormTypeType.self_peer_review,
      userSlug: currentUser.slug,
      reviewSlugs: openReviewSlugs,
    })
  );

  const peerReviewFormsForCurrentUser = useSelector(state =>
    ReviewsSelectors.getReviewFormsByUserSlugAndType(state, {
      formType: FormTypeType.peer_review,
      userSlug: currentUser.slug,
      reviewSlugs: openReviewSlugs,
    })
  );

  const reviewFormsForCurrentUser = selfReviewFormsForCurrentUser.merge(peerReviewFormsForCurrentUser);

  const reviewSlugsWithOpenForms = reviewFormsForCurrentUser
    // eslint-disable-next-line
    ?.filter(form => !Boolean(ReviewFormHelpers.getSubmittedAt(form)))
    .valueSeq()
    .map(form => ReviewFormHelpers.getReviewSlug(form));

  const reviewsWithOpenForms = openReviews
    .filter(review => reviewSlugsWithOpenForms?.toArray().includes(ReviewHelpers.getSlug(review)))
    .filter(review => ReviewHelpers.getState(review) === ReviewState.PEER_REVIEW);

  const showOpenPeerReviews = reviewsWithOpenForms.size > 0;
  const showOpenReviews = openReviewsAsCompilerOrCreator.size > 0;

  const handleChoose360s = () => {
    history.push(PageUrls.COACHING_360_START);
  };

  const handleChooseSelfReview = () => {
    history.push(`${PageUrls.COACHING_360_START}?self=true`);
  };

  const baseClassName = 'OpenReviews';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__section`}>
        <CoachingPageHeader
          title="Open 360 Feedback"
          description="360 feedback offers a full view of how the team is doing."
          size="lg"
          buttons={
            <WpButtonGroup>
              <WpButton variant="secondary" onClick={handleChooseSelfReview}>
                Kick off Self 360
              </WpButton>
              {isManager && <WpButton onClick={handleChoose360s}>Kick off 360 Feedback</WpButton>}
            </WpButtonGroup>
          }
        />
      </div>
      <div className={`${baseClassName}__section`}>
        <CoachingPageHeader title="Open Peer Feedback" description="Peer feedback that need your attention" />
        {showOpenPeerReviews ? (
          <ReviewsCardsContainer reviews={reviewsWithOpenForms} isPeerReviewForms />
        ) : (
          <EmptyStateContainer subTitle="No peer feedback needs your attention right now" inContainer />
        )}
      </div>
      {(isManager || showOpenReviews) && (
        <div className={`${baseClassName}__section`}>
          <CoachingPageHeader title="Open 360 Feedback" description="360 Feedback that you kicked off" />
          {showOpenReviews ? (
            <ReviewsCardsContainer reviews={openReviewsAsCompilerOrCreator} />
          ) : (
            <EmptyStateContainer
              subTitle="No active 360 Feedback"
              inContainer
              button={
                <>
                  {isManager ? (
                    <WpButton variant="secondary" onClick={handleChoose360s}>
                      Kick off 360 Feedback
                    </WpButton>
                  ) : (
                    <WpButton variant="secondary" onClick={handleChooseSelfReview}>
                      Kick off self 360 Feedback
                    </WpButton>
                  )}
                </>
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OpenReviews;
